import { FC, useState } from 'react';
import { orderBy, sortBy } from 'lodash';
import {
  useAvailableOrganisations,
  useMutateAdminUserOrganisation,
} from 'hooks/data/adminUser/useOrganisationMemberships';
import Button from 'components/Button';
import RecordView from 'components/RecordView';
import FieldRow from 'components/FormElements/FieldRow';
import BasicSelect from 'further-ui/components/BasicSelect';
import { Box } from '@mui/material';
import { AdminUserRealm } from 'further-types/admin-user';
import { SortOrder } from 'further-types/utils';

type Props = {
  adminUserId: string;
  currentOrgMemberships: Array<string>;
};

const AddOrganisationMembership: FC<Props> = ({
  adminUserId,
  currentOrgMemberships,
}) => {
  const [auth0OrgId, setAuth0OrgId] = useState<string>();
  const [auth0RoleId, setAuth0RoleId] = useState<string>();

  const { data: allOrganisations } = useAvailableOrganisations();
  const { create } = useMutateAdminUserOrganisation(adminUserId);

  const onSelectOrganisation = (id: string) => {
    setAuth0OrgId(id);
    setAuth0RoleId(undefined);
  };

  const addUserToOrganisation = () => {
    create.mutate(
      //@ts-expect-error
      { auth0OrgId, auth0RoleId },
      {
        onSuccess: () => {
          setAuth0OrgId(undefined);
          setAuth0RoleId(undefined);
        },
      },
    );
  };

  const availableRoles = allOrganisations?.find(
    (org) => org.auth0OrgId === auth0OrgId,
  )?.roles;
  const sortedAvailableRoles = orderBy(
    availableRoles,
    (role) => role.permissions.length,
    SortOrder.Desc,
  );

  const availableOrganisations = allOrganisations?.filter(
    (org) => !currentOrgMemberships.includes(org.auth0OrgId),
  );

  const seisOrgs = sortBy(
    availableOrganisations?.filter((org) => org.realm === AdminUserRealm.Eis),
    'firmName',
  );

  const lpOrgs = sortBy(
    availableOrganisations?.filter((org) => org.realm === AdminUserRealm.Lp),
    'firmName',
  );

  const superOrg = availableOrganisations?.find(
    (org) => org.realm === AdminUserRealm.Super,
  );

  const organisationOptions = [
    ...(seisOrgs?.length
      ? [
          { value: '__header_seis__', label: '— S/EIS —', disabled: true },
          ...seisOrgs.map((org) => ({
            label: org.firmName ?? '',
            value: org.auth0OrgId,
          })),
        ]
      : []),
    ...(lpOrgs?.length
      ? [
          { value: '__header_lp__', label: '— LP —', disabled: true },
          ...lpOrgs.map((org) => ({
            label: org.firmName ?? '',
            value: org.auth0OrgId,
          })),
        ]
      : []),
    ...(superOrg
      ? [
          {
            value: '__header_super__',
            label: '— Further (super-admin only) —',
            disabled: true,
          },
          {
            label: superOrg.firmName ?? '',
            value: superOrg.auth0OrgId,
          },
        ]
      : []),
  ];

  return (
    <RecordView>
      <FieldRow title="Organisation">
        <BasicSelect
          options={organisationOptions}
          value={auth0OrgId}
          onChange={onSelectOrganisation}
          fullWidth
          placeholder="Select organisation"
          noOptionsMessage="No organisations available"
        />
      </FieldRow>
      <FieldRow title="Role">
        <Box display="flex" gap={4}>
          <BasicSelect
            value={auth0RoleId}
            onChange={setAuth0RoleId}
            options={
              sortedAvailableRoles?.map((role) => ({
                label: role.name,
                value: role.auth0RoleId,
                description: role.description ?? '-',
              })) ?? []
            }
            disabled={!auth0OrgId}
            fullWidth
            noOptionsMessage="No roles available"
            placeholder="Select role"
          />
          <Button
            variant="contained"
            disabled={!auth0OrgId || !auth0RoleId || create.isPending}
            onClick={addUserToOrganisation}
          >
            Add
          </Button>
        </Box>
      </FieldRow>
    </RecordView>
  );
};

export default AddOrganisationMembership;
